import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCheck, FiArrowRight, FiPlus, FiMinus } from 'react-icons/fi';
import { BrainIcon, SparkleIcon, ChartIcon, SyncIcon } from './components/Icons';
import ParticleBackground from './components/ParticleBackground';
import HowItWorks from './components/HowItWorks';
import IronManInterface from './components/IronManInterface';
import ContactForm from './components/ContactForm';
import Analytics from './components/Analytics';
import { CookieConsentProvider } from './context/CookieConsentContext';
import CookieConsentBanner from './components/CookieConsent/CookieConsentBanner';
import { HelmetProvider } from 'react-helmet-async';

// SEO Components
import MetaTags from './components/SEO/MetaTags';
import { OrganizationSchema, ProductSchema, WebsiteSchema, FAQSchema } from './components/SEO/SchemaMarkup';

// Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import AboutUs from './pages/AboutUs';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogPostEthics from './pages/BlogPostEthics';
import Support from './pages/Support';
import Positions from './pages/Positions';

import './App.css';

// Navigation Component
const Navbar = () => {
  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary-600 text-white px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>

      <nav className="fixed top-0 left-0 right-0 z-50 glass-card bg-white/70 backdrop-blur-glass" aria-label="Main navigation">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center text-2xl font-display font-bold text-gradient"
              aria-label="xdimension - Home"
            >
              <img
                src="/logo.png"
                alt="xdimension logo"
                className="h-12 w-auto object-contain"
                style={{ marginRight: '-20px' }}
              />
              <span className="-ml-10">xdimension</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a
              href="/#features"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="View features section"
            >
              Features
            </a>
            <a
              href="/#how-it-works"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="Learn how it works"
            >
              How It Works
            </a>
            <a
              href="/#pricing"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="View pricing plans"
            >
              Pricing
            </a>
            <Link
              to="/about"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="About xdimension"
            >
              About
            </Link>
            <Link
              to="/blog"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="Read our blog"
            >
              Blog
            </Link>
            <Link
              to="/support"
              className="text-neutral-dark/80 hover:text-primary-600 transition-colors"
              aria-label="Get support"
            >
              Support
            </Link>
          </div>

          <div>
            <a
              href="/#get-started"
              className="btn-primary"
              aria-label="Get started with Ainous"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>
    </>
  );
};

// Hero Section Component
const Hero = () => {
  return (
    <section className="min-h-screen flex items-center pt-20 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="w-full md:w-1/2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="small-caps text-primary-600 block mb-2">The Future of AI Companionship</span>
              <h1 className="mb-4">Your <span className="text-gradient">AI Partner</span> in Daily Life</h1>
              <p className="text-xl md:text-2xl text-neutral-dark/80 mb-8">
                xdimension brings Ainous, advanced AI assistants in elegant robot hardware, to enhance your daily experiences and productivity.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a href="#pricing" className="btn-primary">
                  Pre-order Ainous
                  <FiArrowRight className="ml-2" />
                </a>
                <a href="#how-it-works" className="btn-outline">
                  See How It Works
                </a>
              </div>
            </motion.div>
          </div>
          
          <div className="w-full md:w-1/2">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="animate-float"
            >
              <img
                src="/hero-image.jpg"
                alt="Ainous AI Assistant Robot - Futuristic AI companion with elegant design"
                className="rounded-2xl shadow-rim w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Component
const Features = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const features = [
    {
      title: "Advanced Contextual Understanding",
      description: "Ainous understands the nuances of human conversation and remembers your preferences across interactions.",
      icon: <BrainIcon />
    },
    {
      title: "Elegant Hardware Design",
      description: "Beautiful, minimalist hardware that seamlessly integrates into your home or office environment.",
      icon: <SparkleIcon />
    },
    {
      title: "Personalized Learning",
      description: "Ainous adapts to your habits, preferences, and needs, becoming more helpful over time.",
      icon: <ChartIcon />
    },
    {
      title: "Seamless Integration",
      description: "Works with your existing smart home devices, applications, and daily workflows.",
      icon: <SyncIcon />
    }
  ];
  
  return (
    <section id="features" className="section relative overflow-hidden">
      {/* Background effects - removed ParticleBackground for performance */}
      <div className="absolute top-1/2 right-0 w-1/2 h-1/2 bg-secondary-100 rounded-full filter blur-3xl opacity-20"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="small-caps text-primary-600 block mb-2">Features</span>
          <h2 className="mb-6">Smart Features for <span className="text-gradient">Smarter Living</span></h2>
          <p className="text-neutral-dark/80">
            Ainous combines cutting-edge AI with elegant design to create a robot assistant that enhances your life in meaningful ways.
          </p>
        </div>
        
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="feature-card"
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h4 className="mb-3">{feature.title}</h4>
              <p className="text-neutral-dark/80">{feature.description}</p>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-20">
          <div className="glass-card p-8 md:p-12 rounded-2xl overflow-hidden">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-full md:w-1/2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6 }}
                  className="rounded-xl overflow-hidden"
                >
                  <IronManInterface />
                </motion.div>
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="mb-4">Crafted for <span className="text-gradient">Human Connection</span></h3>
                <p className="mb-6">
                  Unlike traditional AI interfaces, Ainous is designed to create a natural, intuitive experience that feels like interacting with a helpful friend rather than a machine.
                </p>
                <ul className="space-y-3">
                  {["Natural conversation flow", "Emotional intelligence", "Contextual awareness", "Learning from interactions"].map((item, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.3, delay: 0.4 + (i * 0.1) }}
                      className="flex items-center"
                    >
                      <FiCheck className="text-primary-600 mr-2" />
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials
const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const testimonials = [
    {
      quote: "Ainous has transformed how I organize my day. It's like having a personal assistant who actually knows what I need before I do.",
      author: "Sarah J.",
      title: "Product Designer"
    },
    {
      quote: "The natural conversation flow makes it feel like I'm talking to a friend, not a robot. It's the first AI assistant that doesn't make me feel awkward.",
      author: "Michael T.",
      title: "Tech Entrepreneur"
    },
    {
      quote: "As someone with accessibility needs, having an assistant that understands natural language has been life-changing for my daily tasks.",
      author: "Elena R.",
      title: "Accessibility Advocate"
    }
  ];
  
  return (
    <section className="section relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-primary-100 rounded-full filter blur-3xl opacity-30"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="small-caps text-primary-600 block mb-2">Testimonials</span>
          <h2 className="mb-6">What Our <span className="text-gradient">Early Users Say</span></h2>
          <p className="text-neutral-dark/80">
            Hear from our community of early adopters who've experienced the Ainous difference.
          </p>
        </div>
        
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="testimonial-card"
            >
              <div className="text-4xl text-primary-500 mb-4">"</div>
              <p className="italic text-lg mb-6">{testimonial.quote}</p>
              <div className="mt-auto">
                <p className="font-semibold">{testimonial.author}</p>
                <p className="text-sm text-neutral-dark/70">{testimonial.title}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-20 text-center">
          <div className="glass-card p-8 md:p-12 rounded-2xl">
            <h3 className="mb-8">Ready to Experience the <span className="text-gradient">Future of AI?</span></h3>
            <a href="#pricing" className="btn-primary text-lg px-8 py-4">
              Get Your Ainous Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const Pricing = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const plans = [
    {
      name: "Essential",
      price: "$7**",
      description: "Perfect for individuals seeking a personal AI assistant",
      features: [
        "Basic AI assistant functionalities",
        "Smart home integration",
        "Voice and text interaction",
        "Monthly software updates",
        "1-year hardware warranty"
      ],
      cta: "Pre-order Now",
      popular: false
    },
    {
      name: "Premium",
      price: "$1***",
      description: "Enhanced capabilities for power users and professionals",
      features: [
        "Advanced AI capabilities",
        "Priority processing",
        "Custom skill development",
        "Weekly software updates",
        "2-year comprehensive warranty",
        "Premium support"
      ],
      cta: "Pre-order Now",
      popular: true
    },
    {
      name: "Family",
      price: "$1***",
      description: "Multiple robot solution for households",
      features: [
        "2 connected Ainous assistants",
        "Shared knowledge between devices",
        "Family recognition system",
        "Advanced parental controls",
        "3-year comprehensive warranty",
        "24/7 priority support"
      ],
      cta: "Pre-order Now",
      popular: false
    }
  ];
  
  return (
    <section id="pricing" className="section bg-neutral-light relative overflow-hidden">
      {/* Background effects - simplified for performance */}
      <div className="absolute inset-0 bg-grid opacity-30"></div>
      
      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="small-caps text-primary-600 block mb-2">Pricing Plans</span>
          <h2 className="mb-6">Find Your Perfect <span className="text-gradient">Ainous</span></h2>
          <p className="text-neutral-dark/80">
            Choose the right plan for your needs. All plans include the physical Ainous assistant and access to our AI platform.
          </p>
        </div>
        
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`glass-card p-8 border-2 relative ${plan.popular ? 'border-primary-400 shadow-rim' : 'border-white/40'}`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-primary-600 text-white text-sm font-semibold py-1 px-3 rounded-bl-lg rounded-tr-xl">
                  Most Popular
                </div>
              )}
              
              <h4 className="mb-2">{plan.name}</h4>
              <div className="mb-4">
                <span className="text-4xl font-bold">{plan.price}</span>
              </div>
              <p className="text-neutral-dark/70 mb-6">{plan.description}</p>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <FiCheck className="text-primary-600 mt-1 mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-auto">
                <a 
                  href="#get-started" 
                  className={plan.popular ? 'btn-primary w-full justify-center' : 'btn-outline w-full justify-center'}
                >
                  {plan.cta}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <p className="text-neutral-dark/70">
            All plans include free shipping. Pre-orders expected to ship in Q2 2027. <br />
            Questions about our plans? <a href="mailto:sales@xdimension.ai" className="text-primary-600 hover:underline font-semibold" onClick={(e) => { e.preventDefault(); window.location.href = 'mailto:sales@xdimension.ai'; }}>Contact our sales team</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQ = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [activeIndex, setActiveIndex] = React.useState(null);
  
  const faqs = [
    {
      question: "How does Ainous compare to voice assistants like Alexa or Google Assistant?",
      answer: "Ainous offers a more comprehensive experience through physical embodiment, advanced contextual understanding, and personalized learning. Unlike voice-only assistants, Ainous can move, express personality through motion, and maintain continuous awareness of your environment for more natural interactions."
    },
    {
      question: "What about privacy? Is my data secure?",
      answer: "Privacy is core to our design philosophy. Ainous processes most data locally on the device, minimizing cloud dependency. You have complete control over what information is stored, shared, or deleted. We never sell your data to third parties, and all cloud communications are encrypted end-to-end."
    },
    {
      question: "How long does the battery last?",
      answer: "Ainous can operate for approximately 18 hours on a single charge with normal usage. The included charging dock makes it easy to maintain power, and the robot automatically returns to its dock when battery levels are low."
    },
    {
      question: "Can I customize my Ainous's appearance?",
      answer: "Yes! The Essential model comes in our signature white finish, while Premium and Family models offer customizable color panels that can be easily swapped. We also offer seasonal and limited edition designs for purchase separately."
    },
    {
      question: "What happens if I need technical support?",
      answer: "All plans include access to our support portal with comprehensive documentation, video tutorials, and community forums. Premium and Family plans include priority email and chat support, with Family plans offering 24/7 phone support for critical issues."
    },
    {
      question: "Will Ainous receive updates and new features?",
      answer: "Absolutely! We regularly release software updates that improve functionality and add new features. Essential plans receive monthly updates, while Premium and Family plans receive weekly enhancements and early access to new capabilities."
    }
  ];
  
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  return (
    <section id="faq" className="section relative overflow-hidden">
      {/* Background effects - simplified for performance */}

      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="small-caps text-primary-600 block mb-2">FAQ</span>
          <h2 className="mb-6 leading-snug">Frequently Asked <span className="text-gradient">Questions</span></h2>
          <p className="text-neutral-dark/80">
            Find answers to common questions about Ainous and our AI assistant technology.
          </p>
        </div>
        
        <div ref={ref} className="max-w-4xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="mb-4 overflow-hidden"
            >
              <motion.button
                onClick={() => toggleFAQ(index)}
                className="w-full glass-card p-6 flex justify-between items-center text-left hover:shadow-key transition-all duration-300"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <h5 className="pr-8">{faq.question}</h5>
                <motion.div
                  animate={{ rotate: activeIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="flex-shrink-0"
                >
                  {activeIndex === index ? 
                    <FiMinus className="text-primary-600" /> : 
                    <FiPlus className="text-primary-600" />
                  }
                </motion.div>
              </motion.button>
              
              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, scaleY: 0 }}
                    animate={{ 
                      opacity: 1, 
                      height: "auto", 
                      scaleY: 1,
                      transition: {
                        height: { duration: 0.4, ease: "easeInOut" },
                        opacity: { duration: 0.25, delay: 0.1 },
                        scaleY: { duration: 0.4, ease: "easeInOut" }
                      }
                    }}
                    exit={{ 
                      opacity: 0, 
                      height: 0, 
                      scaleY: 0,
                      transition: {
                        height: { duration: 0.4, ease: "easeInOut" },
                        opacity: { duration: 0.15 },
                        scaleY: { duration: 0.4, ease: "easeInOut" }
                      }
                    }}
                    className="glass-card mt-1 border-t-0 rounded-t-none origin-top"
                    style={{ overflow: "hidden" }}
                  >
                    <motion.div
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ 
                        y: 0, 
                        opacity: 1,
                        transition: { delay: 0.2, duration: 0.3 }
                      }}
                      exit={{ 
                        y: -10, 
                        opacity: 0,
                        transition: { duration: 0.15 }
                      }}
                      className="p-6"
                    >
                      <p className="text-neutral-dark/80 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact/CTA Section
const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  return (
    <section id="get-started" className="section bg-neutral-dark text-neutral-light relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-10"></div>
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary-900 rounded-full filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-secondary-900 rounded-full filter blur-3xl opacity-20"></div>

      <div className="container mx-auto relative z-10">
        <div ref={ref} className="max-w-4xl mx-auto glass-card bg-neutral-dark/50 border-neutral-light/10 p-8 md:p-12 rounded-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="mb-6">Ready to Meet Your <span className="text-gradient">AI Partner?</span></h2>
            <p className="text-xl text-neutral-light/80 max-w-3xl mx-auto">
              Join our community of forward-thinking individuals embracing the future of AI companionship. Pre-order your Ainous today.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ContactForm />
            <p className="mt-4 text-sm text-neutral-light/60 text-center">
              By submitting, you agree to our <Link to="/terms" className="text-primary-400 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-primary-400 hover:underline">Privacy Policy</Link>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-neutral-dark text-neutral-light pt-20 pb-10 relative overflow-hidden">
      {/* Background effects - simplified for performance */}

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
          <div className="md:col-span-1">
            <Link to="/" className="text-2xl font-display font-bold text-gradient mb-6 inline-flex items-center">
              <img
                src="/logo.png"
                alt="xdimension logo"
                className="h-12 w-auto object-contain"
                style={{ marginRight: '-20px' }}
              />
              <span className="-ml-10">xdimension</span>
            </Link>
            <p className="text-neutral-light/70 mb-6">
              Bringing AI companions to life through advanced technology and elegant design.
            </p>
            <div className="flex space-x-4">
              <a href="https://twitter.com" className="text-neutral-light/70 hover:text-primary-400 transition-colors" aria-label="Twitter">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                </svg>
              </a>
              <a href="https://instagram.com" className="text-neutral-light/70 hover:text-primary-400 transition-colors" aria-label="Instagram">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd"></path>
                </svg>
              </a>
              <a href="https://linkedin.com" className="text-neutral-light/70 hover:text-primary-400 transition-colors" aria-label="LinkedIn">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h5 className="text-lg font-semibold mb-4">Product</h5>
            <ul className="space-y-2">
              <li><a href="/#features" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Features</a></li>
              <li><a href="/#how-it-works" className="text-neutral-light/70 hover:text-primary-400 transition-colors">How It Works</a></li>
              <li><a href="/#pricing" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Pricing</a></li>
              <li><a href="/#faq" className="text-neutral-light/70 hover:text-primary-400 transition-colors">FAQ</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="text-lg font-semibold mb-4">Company</h5>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-neutral-light/70 hover:text-primary-400 transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Blog</Link></li>
              <li><Link to="/support" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Support</Link></li>
              <li><Link to="/positions" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Careers</Link></li>
            </ul>
          </div>
          
          <div>
            <h5 className="text-lg font-semibold mb-4">Legal</h5>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Terms of Service</Link></li>
              <li><Link to="/cookies" className="text-neutral-light/70 hover:text-primary-400 transition-colors">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-neutral-light/10 text-center md:text-left md:flex md:justify-between md:items-center">
          <p className="text-neutral-light/50 text-sm">
            &copy; {new Date().getFullYear()} xdimension AI. All rights reserved.
          </p>
          <div className="mt-4 md:mt-0">
            <Link to="/privacy" className="text-neutral-light/50 hover:text-primary-400 transition-colors text-sm mr-6">
              Privacy
            </Link>
            <Link to="/terms" className="text-neutral-light/50 hover:text-primary-400 transition-colors text-sm">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Homepage Component
const Homepage = () => {
  // Schema.org structured data for homepage
  const faqList = [
    {
      question: "How does Ainous compare to voice assistants like Alexa or Google Assistant?",
      answer: "Ainous offers a more comprehensive experience through physical embodiment, advanced contextual understanding, and personalized learning. Unlike voice-only assistants, Ainous can move, express personality through motion, and maintain continuous awareness of your environment for more natural interactions."
    },
    {
      question: "What about privacy? Is my data secure?",
      answer: "Privacy is core to our design philosophy. Ainous processes most data locally on the device, minimizing cloud dependency. You have complete control over what information is stored, shared, or deleted. We never sell your data to third parties, and all cloud communications are encrypted end-to-end."
    },
    {
      question: "How long does the battery last?",
      answer: "Ainous can operate for approximately 18 hours on a single charge with normal usage. The included charging dock makes it easy to maintain power, and the robot automatically returns to its dock when battery levels are low."
    }
  ];

  return (
    <>
      <MetaTags 
        title="xdimension | Your AI Partner in Daily Life" 
        description="xdimension brings Ainous, advanced AI assistants in elegant robot hardware, to enhance your daily experiences and productivity."
        url="https://xdimension.ai"
      />
      <OrganizationSchema />
      <ProductSchema />
      <WebsiteSchema />
      <FAQSchema faqs={faqList} />
      
      <Hero />
      <Features />
      <HowItWorks />
      <Testimonials />
      <Pricing />
      <FAQ />
      <Contact />
    </>
  );
};

// Main App Component
function App() {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  const handleAcceptAll = () => {
    console.log('All cookies accepted');
    // Analytics tracking can be initialized here if needed
  };

  const handleRejectAll = () => {
    console.log('All cookies rejected except necessary');
    // Any cleanup of tracking here if needed
  };

  const handleCustomizePreferences = (newPrefs) => {
    console.log('Cookie preferences customized:', newPrefs);
    setCookiePreferences(newPrefs);
    // Enable/disable tracking based on preferences
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      const navbar = document.querySelector('nav');
      if (navbar) {
        if (scrolled) {
          navbar.classList.add('shadow-key');
        } else {
          navbar.classList.remove('shadow-key');
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <Router>
          <div className="App relative">
            {/* Single global ParticleBackground covering entire page */}
            <div className="fixed inset-0 z-0 pointer-events-none">
              <ParticleBackground />
            </div>

            <div className="relative z-10">
              <Navbar />

              <Routes>
                <Route path="/" element={<Homepage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/ethics/:slug" element={<BlogPostEthics />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/support" element={<Support />} />
                <Route path="/positions" element={<Positions />} />
              </Routes>

              <Footer />

              {/* Cookie Consent Banner */}
              <CookieConsentBanner
                onAccept={handleAcceptAll}
                onReject={handleRejectAll}
                onCustomize={handleCustomizePreferences}
                preferences={cookiePreferences}
                setPreferences={setCookiePreferences}
              />

              {/* Analytics (only loads if consent given) */}
              <Analytics />
            </div>
          </div>
        </Router>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
